<template>
    <span ref="root" class="async-cell">
        <text-skeleton v-if="loading" :lines="1" />

        <!-- The request failed: say so, and offer to ask again -->
        <span v-else-if="failed" class="async-cell__error">
            <i class="fa-solid fa-triangle-exclamation"></i>
            <span>{{ errorText }}</span>
            <button
                type="button"
                class="async-cell__retry"
                v-b-tooltip.hover
                :title="__('load_failed_retry')"
                @click.stop="run"
            >
                <i class="fa-solid fa-rotate-right"></i>
            </button>
        </span>

        <slot v-else :value="value">{{ value }}</slot>
    </span>
</template>

<script>
import { onBeforeUnmount, ref, watch } from 'vue'
import { useElementVisibility } from '@vueuse/core'
import TextSkeleton from '@/components/commons/TextSkeleton.vue'

/**
 * A table cell whose value has to be fetched.
 *
 *   <async-cell :watch-key="truck.id" :load="(signal) => dailyDistance(truck, signal)">
 *       <template #default="{ value }">{{ value }} km</template>
 *   </async-cell>
 *
 * The contract:
 * - nothing is requested until the cell is scrolled into view — a fleet is
 *   hundreds of rows and a dozen are on screen;
 * - a skeleton stands in while the answer is on its way;
 * - `watch-key` changing means the row now stands for something else, so the
 *   value is fetched again;
 * - unmounting aborts the request through the signal handed to `load`, and any
 *   answer that lands afterwards is dropped.
 *
 * Whether the abort actually cancels anything is up to `load`: a request that
 * belongs to this cell alone should honour the signal, one shared through a
 * cache (several rows waiting on the same answer) should ignore it.
 */
export default {
    name: 'AsyncCell',
    components: {
        TextSkeleton
    },
    props: {
        // `(signal) => Promise<value>`; the signal is aborted on unmount
        load: {
            type: Function,
            required: true
        },
        // Ask again when this changes: the row now stands for something else
        watchKey: {
            type: [String, Number],
            default: null
        },
        // Stands in for the value when the request fails
        errorText: {
            type: String,
            default: '!'
        }
    },
    setup(props) {
        const root = ref(null)
        const visible = useElementVisibility(root)

        const value = ref(null)
        const loading = ref(false)
        const failed = ref(false)

        let controller = null

        function abort() {
            if (controller) controller.abort()
            controller = null
        }

        async function run() {
            if (!visible.value) return

            abort()
            controller = new AbortController()

            const { signal } = controller

            loading.value = true
            failed.value = false

            try {
                const result = await props.load(signal)

                if (!signal.aborted) value.value = result
            } catch (error) {
                // Aborting is the row scrolling away, not a failure
                if (!signal.aborted) {
                    failed.value = true
                    console.log(error)
                }
            } finally {
                if (!signal.aborted) loading.value = false
            }
        }

        // Not `load`: the parent hands over a fresh closure on every render, and
        // the row itself has not changed.
        watch([visible, () => props.watchKey], run, { immediate: true })
        onBeforeUnmount(abort)

        return { root, value, loading, failed, run }
    }
}
</script>

<style scoped>
.async-cell {
    display: inline-block;
    min-width: 60px;
}

.async-cell__error {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: var(--danger, #dc3545);
}

.async-cell__retry {
    border: 0;
    background: transparent;
    padding: 0;
    line-height: 1;
    color: inherit;
    cursor: pointer;
}
</style>
